/**
 * Metadata editor panel state: open tabs, active tab, accepted code (for diff),
 * and initial content for virtual files. Also provides openTab and close handlers.
 *
 * OrgPilot local copy — imports OpenFile from local MetadataEditorPanel.
 *
 * TAB ORDER is the array order of `openFiles`, mutated only by `reorderTabs`.
 * The tab strip proposes an order; this hook decides it, so the DOM can never be
 * the source of truth for what the user sees.
 *
 * PREVIEW vs PERMANENT tabs follow VS Code, via four distinct actions rather than
 * one `openTab` with flags — the intents genuinely differ:
 *
 *   previewTab   single click: reuse the one preview slot
 *   openTab      double click, or an agent-initiated open: permanent
 *   promoteTab   a preview becomes permanent (double-click, or its first edit)
 *   activateTab  select an open tab, changing nothing else
 *
 * `activateTab` exists precisely so that selecting a preview tab does not promote
 * it, which is what happens if activation is routed through `openTab`.
 * The rules themselves live in `openFileList` so they can be tested without
 * mounting the IDE.
 *
 * ORGANIZATION SCOPING
 * --------------------
 * These tabs hold one organization's file ids AND their content (`initialContent`
 * seeds Monaco). This state is component-local and the IDE panel is not remounted
 * on an org switch, so without the teardown below, switching orgs from the header
 * left Org A's file open, with Org A's source on screen, labelled as Org B.
 *
 * Unlike `metadataEditorStore` these are discarded rather than parked: they are
 * ephemeral, chat-driven documents (artifact ids, pre-save virtual ids) whose
 * content is re-seeded by the run that produced them, not a workspace the user
 * curated and expects to find again.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { subscribeToOrgChange } from "@/lib/org-scope";
import { applyTabOrder } from "@ncoder/code-editor/tab-order";
import {
  openFilePermanently,
  previewOpenFile,
  promoteOpenFile,
} from "./openFileList";
import type { OpenFile } from "./MetadataEditorPanel";

export function useMetadataEditorState(setPendingFile: (f: { id: string | number; name: string } | null) => void) {
  const [openFiles, setOpenFiles] = useState<OpenFile[]>([]);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [acceptedCode, setAcceptedCode] = useState<string | null>(null);
  const [initialContent, setInitialContent] = useState<Record<string, string>>({});

  // Mirrors `openFiles` for the callbacks that need to read it without depending
  // on it (and so without being recreated on every open).
  const openFilesRef = useRef<OpenFile[]>(openFiles);
  openFilesRef.current = openFiles;

  /**
   * Open a file permanently — a double-click in the sidebar, or a deliberate open
   * by the agent (artifact, refactor target). A file already open as a preview is
   * promoted rather than reopened, so its loaded content and position survive.
   */
  const openTab = useCallback((entry: OpenFile) => {
    setOpenFiles((prev) => openFilePermanently(prev, entry));
    setActiveFileId(entry.id);
  }, []);

  /**
   * Open a file as a PREVIEW — a single click in the sidebar.
   *
   * Reuses the one preview slot, so clicking through a list of files leaves one
   * tab instead of twenty. See `openFileList` for why this cannot discard unsaved
   * work.
   */
  const previewTab = useCallback((entry: OpenFile) => {
    setOpenFiles((prev) => previewOpenFile(prev, entry));
    setActiveFileId(entry.id);
  }, []);

  /** Make a preview tab permanent: double-click on the tab, or its first edit. */
  const promoteTab = useCallback((fileId: string) => {
    setOpenFiles((prev) => promoteOpenFile(prev, fileId));
  }, []);

  /**
   * Activate an already-open tab. Distinct from {@link openTab} on purpose:
   * selecting a preview tab must not make it permanent, which is what would
   * happen if activation went through the "open it properly" path.
   */
  const activateTab = useCallback((fileId: string) => {
    // Reads a ref rather than nesting a set inside `setOpenFiles`'s updater:
    // updaters must stay pure, or StrictMode's double invocation turns this into
    // two activations.
    if (openFilesRef.current.some((f) => f.id === fileId)) setActiveFileId(fileId);
  }, []);

  /**
   * Apply a proposed tab order. `applyTabOrder` ignores ids that have since
   * closed and keeps unmentioned tabs, so a stale payload can never drop a tab.
   * The active file is addressed by id and so is unaffected.
   */
  const reorderTabs = useCallback((orderedIds: string[]) => {
    setOpenFiles((prev) => applyTabOrder(prev, orderedIds, (f) => f.id));
  }, []);

  const handleCloseTab = useCallback(
    (fileId: string) => {
      setOpenFiles((prev) => {
        const remaining = prev.filter((f) => f.id !== fileId);
        // If closing the active tab, switch to the previous or next tab
        setActiveFileId((currentActive) => {
          if (currentActive !== fileId) return currentActive;
          setAcceptedCode(null);
          if (remaining.length === 0) return null;
          const closedIdx = prev.findIndex((f) => f.id === fileId);
          const nextIdx = Math.min(closedIdx, remaining.length - 1);
          return remaining[nextIdx]?.id ?? null;
        });
        return remaining;
      });
    },
    []
  );

  const handleCloseMetadataEditor = useCallback(() => {
    setOpenFiles([]);
    setActiveFileId(null);
    setAcceptedCode(null);
    setPendingFile(null);
  }, [setPendingFile]);

  /** Repoint tab + seeded content from chat/virtual id to backend metadata UUID. */
  const migrateFileId = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;
    setOpenFiles((prev) =>
      prev.map((f) => (f.id === fromId ? { ...f, id: toId } : f)),
    );
    setActiveFileId((cur) => (cur === fromId ? toId : cur));
    setInitialContent((prev) => {
      const next = { ...prev };
      if (next[fromId] !== undefined) {
        next[toId] = next[fromId];
        delete next[fromId];
      }
      return next;
    });
  }, []);

  // Organization switch — drop this organization's tabs, the content seeded for
  // them, and any accepted diff. See the note at the top of the file.
  useEffect(
    () =>
      subscribeToOrgChange(() => {
        setOpenFiles([]);
        setActiveFileId(null);
        setAcceptedCode(null);
        setInitialContent({});
      }),
    [],
  );

  return {
    openFiles,
    activeFileId,
    setActiveFileId,
    acceptedCode,
    setAcceptedCode,
    initialContent,
    setInitialContent,
    openTab,
    previewTab,
    promoteTab,
    activateTab,
    reorderTabs,
    handleCloseTab,
    handleCloseMetadataEditor,
    migrateFileId,
  };
}
